import { useCallback, useMemo, useRef, useState } from 'react';
import {
  listConsultationsForOrg,
  type ConsultationRow,
  type ConsultationFilter,
} from '../infra/consultationsRepo';

type Query = { orgId: string; filter: ConsultationFilter; branchId: string | null };

export function useConsultationsList(
  orgId: string | null | undefined,
  filter: ConsultationFilter = 'all',
  branchId?: string | null,
) {
  const [items, setItems] = useState<ConsultationRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const offsetRef = useRef(0);
  // BUG-12: track request sequence to discard stale responses on rapid filter changes
  const requestIdRef = useRef(0);

  const query = useMemo<Query>(
    () => ({ orgId: orgId ?? '', filter, branchId: branchId ?? null }),
    [orgId, filter, branchId],
  );

  const load = useCallback(async (reset: boolean) => {
    if (!query.orgId) return;
    const requestId = ++requestIdRef.current;
    setLoading(true);
    try {
      const currentOffset = reset ? 0 : offsetRef.current;
      const data = await listConsultationsForOrg(query.orgId, query.filter, {
        limit: 20,
        offset: currentOffset,
      });
      // Discard stale response if a newer request was issued
      if (requestId !== requestIdRef.current) return;
      // #758: filtrar por branch client-side hasta que el RPC soporte p_branch_id
      const filtered = query.branchId
        ? data.filter((c) => c.branch_id === query.branchId)
        : data;
      setItems((prev) => (reset ? filtered : [...prev, ...filtered]));
      setHasMore(data.length === 20);
      offsetRef.current = currentOffset + data.length;
    } catch (e) {
      if (requestId !== requestIdRef.current) return;
      console.error('useConsultationsList', e);
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }, [query]);

  const reload = useCallback(() => {
    offsetRef.current = 0;
    void load(true);
  }, [load]);

  const loadMore = useCallback(() => {
    if (!hasMore || loading) return;
    void load(false);
  }, [hasMore, loading, load]);

  return {
    consultations: items,
    loading,
    hasMore,
    reload,
    loadMore,
  };
}
